import {
  collection,
  Firestore,
  getDocs,
  query,
  where,
} from 'firebase/firestore';

import { isPubliclyVisibleMeeting } from '@/src/helpers/isPubliclyVisibleMeeting';
import { MeetingResponse } from '@/types';

/**
 * Every meeting the public should see within a set of geohash ranges.
 *
 * Lives here rather than inside `useFetchMeetings` for two reasons. It is data
 * access, not view state — and the deactivation filter below is a single call
 * that the repo's no-render-test rule would otherwise leave unpinned. A unit
 * test can pin the predicate; only driving THIS can show it is still wired up,
 * which `scripts/eval/pas63-bulk-commit.ts` does against the emulator after
 * approving a Deactivate proposal.
 */
export const fetchMeetingsInRange = async (
  db: Firestore,
  bounds: [string, string][]
): Promise<MeetingResponse[]> => {
  const meetingsRef = collection(db, 'meetings');
  const promises = bounds.map(([start, end]) =>
    getDocs(
      query(
        meetingsRef,
        where('geohash', '>=', start),
        where('geohash', '<=', end)
      )
    )
  );

  const querySnapshots = await Promise.all(promises);
  const meetings: MeetingResponse[] = [];

  querySnapshots.forEach((snapshot) => {
    snapshot.forEach((doc) =>
      meetings.push({ id: doc.id, ...doc.data() } as MeetingResponse)
    );
  });

  // Deactivated meetings do not belong on the public map. Filtered here rather
  // than in the query because the query is a geohash RANGE: Firestore cannot
  // combine that with an equality on another field without a composite index,
  // and `where('active','==',true)` would silently drop every document
  // predating the field. See `isPubliclyVisibleMeeting`.
  return meetings.filter(isPubliclyVisibleMeeting);
};
